import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminDivisions() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        const { data } = await api.get("/divisions");
        setItems(data);
    }, []);
    useEffect(() => {
        load();
    }, [load]);

    const save = async (d) => {
        setErr("");
        try {
            if (d.id) {
                await api.put(`/divisions/${d.id}`, d);
            } else {
                await api.post("/divisions", d);
            }
            setEditing(null);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare questa divisione?")) return;
        try {
            await api.delete(`/divisions/${id}`);
            await load();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div data-testid="admin-divisions">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <h2 className="op-heading text-3xl">Divisioni</h2>
                    <p className="op-label mt-2">4 livelli del torneo</p>
                </div>
                <button
                    className="op-btn op-btn-primary"
                    onClick={() =>
                        setEditing({ code: "", name: "", description: "", accent_color: "rgb(var(--tc-2bb6ff))", order: items.length + 1 })
                    }
                    data-testid="division-add-btn"
                >
                    + Nuova divisione
                </button>
            </div>

            {err && (
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-sm p-3 mb-4">
                    {err}
                </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
                {items.map((d) => (
                    <div
                        key={d.id}
                        className="op-card p-5"
                        style={{ borderTop: `2px solid ${d.accent_color}` }}
                        data-testid={`division-card-admin-${d.code}`}
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="op-label" style={{ color: d.accent_color }}>
                                    Tier {d.order} · {d.code}
                                </div>
                                <div className="op-heading text-2xl mt-1">{d.name}</div>
                                <p className="text-t8f95a3 text-sm mt-2">{d.description}</p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <button
                                    onClick={() => setEditing(d)}
                                    className="op-btn py-1.5 px-3 text-xs"
                                    data-testid={`division-edit-btn-${d.code}`}
                                >
                                    Modifica
                                </button>
                                <button
                                    onClick={() => remove(d.id)}
                                    className="op-btn op-btn-ghost py-1.5 px-3 text-xs text-tff3b30 hover:text-tff3b30"
                                    data-testid={`division-delete-btn-${d.code}`}
                                >
                                    Elimina
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <DivisionEditor
                    value={editing}
                    onCancel={() => setEditing(null)}
                    onSave={save}
                />
            )}
        </div>
    );
}

function DivisionEditor({ value, onCancel, onSave }) {
    const [d, setD] = useState(value);
    const update = (k, v) => setD((prev) => ({ ...prev, [k]: v }));
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" data-testid="division-editor">
            <div className="op-card w-full max-w-lg p-6">
                <div className="op-label text-t2bb6ff">
                    {d.id ? "// MODIFICA DIVISIONE" : "// NUOVA DIVISIONE"}
                </div>
                <h3 className="op-heading text-2xl mt-2">{d.code || "Nuova"}</h3>
                <div className="mt-5 space-y-3">
                    <div>
                        <label className="op-label block mb-2">Codice</label>
                        <input
                            value={d.code}
                            onChange={(e) => update("code", e.target.value.toUpperCase())}
                            className="op-input"
                            placeholder="TOP"
                            data-testid="division-code-input"
                        />
                    </div>
                    <div>
                        <label className="op-label block mb-2">Nome</label>
                        <input
                            value={d.name}
                            onChange={(e) => update("name", e.target.value)}
                            className="op-input"
                            data-testid="division-name-input"
                        />
                    </div>
                    <div>
                        <label className="op-label block mb-2">Descrizione</label>
                        <textarea
                            value={d.description || ""}
                            onChange={(e) => update("description", e.target.value)}
                            className="op-input min-h-[80px]"
                            data-testid="division-desc-input"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="op-label block mb-2">Colore accento</label>
                            <input
                                type="color"
                                value={d.accent_color}
                                onChange={(e) => update("accent_color", e.target.value)}
                                className="op-input h-12 p-1"
                                data-testid="division-color-input"
                            />
                        </div>
                        <div>
                            <label className="op-label block mb-2">Ordine</label>
                            <input
                                type="number"
                                value={d.order || 0}
                                onChange={(e) => update("order", parseInt(e.target.value) || 0)}
                                className="op-input"
                                data-testid="division-order-input"
                            />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onCancel} className="op-btn" data-testid="division-cancel-btn">
                        Annulla
                    </button>
                    <button onClick={() => onSave(d)} className="op-btn op-btn-primary" data-testid="division-save-btn">
                        Salva
                    </button>
                </div>
            </div>
        </div>
    );
}
